import { FC, useEffect, useState } from 'react';
import { SidePanel } from '@carbon/ibm-products';
import {
  InlineNotification,
  RadioButton,
  RadioButtonGroup,
} from '@carbon/react';
import type { WellsResponse } from 'types/api-responses';
import type { ImportedWellInput } from './types';
import { WellsTableWithFilters } from './wells-table-with-filters';
import { ImportedWellForm } from './imported-well-form';
import styles from './structuring-module.module.scss';
import wellStyles from './wells.module.scss';

// Shared presentational shell for the two well-picker SidePanels (Add Well and
// Change Match). Both offer the same two paths:
//   • "Select from Wells"  — pick an existing Well via the real Query::Wells
//     search (WellsTableWithFilters);
//   • "Create Imported Well" — author an ImportedWell inline (ImportedWellForm).
//
// This component owns the entire shell: the radio path toggle, the gated table
// / form, the inline error, the SidePanel chrome, and all transient state
// (path, selection, validity, pending, error) plus the reset-on-close effect.
// The per-panel differences (copy, the submit action, the payload key, the
// seeded search value) are supplied by the thin wrappers via props — the
// wrapper's `onSubmit` receives the resolved selection and the pending/error
// setters so it can run its own mutation atom and wrap the selection under its
// own payload key, while pending/error remain single-sourced here.

type Path = 'search' | 'create';

/**
 * The resolved user selection, in the shared snake_case inner shape both
 * payloads share. The wrapper nests this under its own key (`well` / `match`).
 */
export type WellSelection =
  | { kind: 'existing_well'; well_id: number }
  | { kind: 'generic_well'; imported_well: ImportedWellInput };

/** Handles the panel hands the wrapper so it can drive pending/error itself. */
export interface WellPickerSubmitHelpers {
  setPending: (pending: boolean) => void;
  setError: (message: string | null) => void;
}

interface Props {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle: string;
  /**
   * Unique DOM prefix for this panel's radio group + buttons, so two pickers
   * never collide on element ids / radio `name` when both are mounted.
   */
  idPrefix: string;
  /**
   * Primary-button label. A single string is used for both paths; pass a
   * `{ search, create }` pair when the label differs by path (Add Well shows
   * "Add Well" vs "Create & Add"). The pending state always shows "Saving…".
   */
  primaryButtonText: string | { search: string; create: string };
  /** Seed the existing-well search box (e.g. the staged well name). */
  initialSearchValue?: string;
  /**
   * Shell root the slide-in SidePanel anchors its margin to. Defaults to the AFE
   * structuring shell; the DO structuring shell passes `.do-structuring-shell`.
   */
  selectorPageContent?: string;
  /**
   * Run the wrapper's mutation for the resolved selection. The panel never
   * sets pending itself — the wrapper toggles it via `helpers` (mirroring the
   * pre-refactor per-panel handlers), so a wrapper that early-returns (e.g. a
   * missing Change Match href) leaves the button un-stuck.
   */
  onSubmit: (
    selection: WellSelection,
    helpers: WellPickerSubmitHelpers
  ) => void;
}

export const WellPickerPanel: FC<Props> = ({
  open,
  onClose,
  title,
  subtitle,
  idPrefix,
  primaryButtonText,
  initialSearchValue,
  selectorPageContent = '.afe-structuring-shell',
  onSubmit,
}) => {
  const [path, setPath] = useState<Path>('search');
  const [selectedWell, setSelectedWell] = useState<WellsResponse | null>(null);
  const [importedForm, setImportedForm] = useState<ImportedWellInput | null>(
    null
  );
  const [genericValid, setGenericValid] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setPath('search');
      setSelectedWell(null);
      setImportedForm(null);
      setGenericValid(false);
      setError(null);
    }
  }, [open]);

  const primaryDisabled =
    pending || (path === 'search' ? !selectedWell : !genericValid);

  const primaryLabel = pending
    ? 'Saving…'
    : typeof primaryButtonText === 'string'
      ? primaryButtonText
      : primaryButtonText[path];

  const handleSubmit = () => {
    let selection: WellSelection | null = null;
    if (path === 'search' && selectedWell) {
      selection = { kind: 'existing_well', well_id: selectedWell.id };
    } else if (path === 'create' && importedForm) {
      selection = { kind: 'generic_well', imported_well: importedForm };
    }
    if (!selection) return;
    onSubmit(selection, { setPending, setError });
  };

  return (
    <SidePanel
      open={open}
      onRequestClose={onClose}
      title={title}
      subtitle={subtitle}
      className={wellStyles.panelLightFields}
      slideIn
      selectorPageContent={selectorPageContent}
      placement="right"
      size="lg"
      actions={[
        { kind: 'secondary' as const, label: 'Cancel', onClick: onClose },
        {
          kind: 'primary' as const,
          label: primaryLabel,
          disabled: primaryDisabled,
          onClick: handleSubmit,
        },
      ]}
    >
      <div className={styles.panelForm}>
        {error && (
          <InlineNotification
            kind="error"
            lowContrast
            title="Error"
            subtitle={error}
            onCloseButtonClick={() => setError(null)}
          />
        )}
        <RadioButtonGroup
          name={`${idPrefix}-path`}
          valueSelected={path}
          onChange={(v) => setPath(String(v) as Path)}
          orientation="horizontal"
          legendText=""
        >
          <RadioButton
            labelText="Select from Wells"
            value="search"
            id={`${idPrefix}-path-search`}
          />
          <RadioButton
            labelText="Create Imported Well"
            value="create"
            id={`${idPrefix}-path-create`}
          />
        </RadioButtonGroup>

        {/* Gate on `open` so the table mounts fresh per open — re-seeding the
            search and never firing a wells query while the panel is hidden. */}
        {open && path === 'search' && (
          <WellsTableWithFilters
            selectedWellId={selectedWell?.id ?? null}
            initialSearchValue={initialSearchValue}
            onSelect={setSelectedWell}
          />
        )}
        {open && path === 'create' && (
          <ImportedWellForm
            onChange={setImportedForm}
            onValidityChange={setGenericValid}
          />
        )}
      </div>
    </SidePanel>
  );
};
